import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Server-side 30-second undo.
 *
 * The API is called immediately (soft-delete on backend); after 30 seconds a
 * backend cleanup job hard-deletes the task permanently. If the user clicks
 * Undo before that, we call the restore endpoint.
 *
 * Because deletion is finalized on the server, closing the browser tab
 * mid-window still results in a permanent delete.
 *
 *   deleteTaskWithUndo({ task, onHide, onRestore })
 *   bulkDeleteTasksWithUndo({ tasks, onHide, onRestore })
 */
export async function deleteTaskWithUndo({ task, onHide, onRestore }) {
  try {
    await api.delete(`/tasks/${task.id}`);
  } catch (err) {
    toast.error(formatApiError(err));
    return;
  }
  onHide([task]);
  toast(`Deleted "${task.title}"`, {
    description: "Permanently removed in 30 seconds.",
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          await api.post(`/tasks/${task.id}/restore`);
          onRestore([task]);
          toast.success("Restored");
        } catch (err) { toast.error(formatApiError(err)); }
      },
    },
    duration: 30000,
  });
}

export async function bulkDeleteTasksWithUndo({ tasks, onHide, onRestore }) {
  if (!tasks || tasks.length === 0) return;
  const ids = tasks.map((t) => t.id);
  try {
    await api.post(`/tasks/bulk-delete`, { task_ids: ids });
  } catch (err) {
    toast.error(formatApiError(err));
    return;
  }
  onHide(tasks);
  const label = tasks.length === 1
    ? `Deleted "${tasks[0].title}"`
    : `Deleted ${tasks.length} tasks`;
  toast(label, {
    description: "Permanently removed in 30 seconds.",
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          await api.post(`/tasks/bulk-restore`, { task_ids: ids });
          onRestore(tasks);
          toast.success("Restored");
        } catch (err) { toast.error(formatApiError(err)); }
      },
    },
    duration: 30000,
  });
}
